import { useCallback, useMemo } from 'react'
import { useAudioEngine, KITS, type KitId } from '@/hooks/useAudioEngine'
import { usePlatform } from '@/hooks/usePlatform'
import { useWebMidi } from '@/hooks/useWebMidi'
import { useMidiStore, VIRTUAL_SEQUENCER_INPUT_ID } from '@/stores/midiStore'
import { useUIStore } from '@/stores/uiStore'
import { useAllSamples } from '@/hooks/useSampleUpload'
import { useEffectiveMidi } from '@/hooks/useEffectiveMidi'
import {
  selectCurrentContainer,
  selectCurrentProject,
  useSequencerStore,
  keysModeNoteFor,
  midiNoteName,
  PADS_PER_PROJECT,
  type MidiPadDef,
  type StepCell,
  type Envelope,
  type StepRoll,
  type PadMidiRouting,
} from '@/stores/sequencerStore'
import { resolvePad, type ResolvedPad } from '@/lib/resolvePad'
import { scrambledSliceId } from '@/lib/scramblerRandomizer'
import {
  resolveAutomationAtTick,
  stepToTick,
  extractPadPresetOverrides,
} from '@/lib/automation'
import { dbToLinear } from '@/lib/sampleNormalize'

const MIDI_VELOCITY = 100

/** Multiply sample / slice / pad gain-in-dB into a single linear
 *  factor for the audio velocity. Missing stages default to 0 dB
 *  (unity). Clamped to 0..4 so a stacked +6 dB per stage (+18 dB
 *  total) can't send velocity through the roof. */
function gainStackLinear(
  sampleDb?: number,
  sliceDb?: number,
  padDb?: number,
): number {
  const linear =
    dbToLinear(sampleDb ?? 0) *
    dbToLinear(sliceDb ?? 0) *
    dbToLinear(padDb ?? 0)
  return Math.max(0, Math.min(4, linear))
}

export interface FireOpts {
  velocity?: number
  pitch?: number
  fx?: StepCell['fx']
  length?: number
  sendMidi?: boolean
  focusOnClick?: boolean
  reverse?: boolean
  timeStretch?: number
  envelope?: Partial<Envelope>
  /** Step index this fire originated from. Used to sample
   *  automation lanes at the right tick. Optional — ad-hoc
   *  fires (UI auditions) pass no step and automation is a
   *  no-op for them. */
  stepIndex?: number
}

export interface FirePadOpts extends FireOpts {
  roll?: StepRoll
}

/**
 * Shared sequencer trigger pipeline. Reads every input (current
 * pattern, kit, effective MIDI routing, output latency, samples)
 * directly from stores + hooks so the returned `firePad` /
 * `fireMidiPad` functions can be called from:
 *
 *   - SequencerPage pad clicks / incoming MIDI notes
 *   - The root-level PlaybackProvider's scheduler tick
 *   - (Future) hotkey shortcuts, MIDI Learn auditions, etc.
 *
 * Keeping this in one hook means there's exactly one source of truth
 * for "what does pad N play when it fires" — cell data, per-pad
 * routing, Keys Mode override, roll fan-out, MIDI note-off timing,
 * per-output latency compensation. Previously all of this lived in
 * SequencerPage, which meant the scheduler stopped firing when the
 * user navigated away.
 */
export function useSequencerTriggers() {
  const {
    resume,
    trigger,
    triggerInstrument,
    attackInstrument,
    releaseInstrument,
    currentTime,
  } = useAudioEngine()
  const { dispatchVirtualMidi, dispatchToHardwareOutput } = useWebMidi()
  const padNoteBindings = useMidiStore(s => s.padNoteBindings)
  const { outputLatencyMs } = useEffectiveMidi()
  const project = useSequencerStore(selectCurrentProject)
  const container = useSequencerStore(selectCurrentContainer)
  const setFocusedPad = useSequencerStore(s => s.setFocusedPad)
  const scramblerParams = useSequencerStore(s => s.scramblerParams)
  // Per-pad mute map for the active bank. `padMuted[padIndex] ===
  // true` ⇒ scheduler silently skips that pad. Cell focus + edits
  // keep working while muted so users can prep a drop without
  // unmuting mid-pattern.
  const activeBankId = container?.activeBankId ?? null
  const padMutedForActiveBank = useSequencerStore(s =>
    activeBankId ? (s.padMuted[activeBankId] ?? {}) : {},
  )
  // Scrub-Pad bank override (PR 5). When the Scrub Pad is engaged
  // and its target bank matches the active bank, suppress pad audio
  // so the scrub pad takes over the drum/loop part mid-performance.
  // Session-only signal written by the ScrubPad component.
  const scrubEngaged = useUIStore(s => s.scrubEngaged)
  const scrubTargetBankId = useUIStore(s => s.scrubTargetBankId)
  const scrubOverrideActive =
    scrubEngaged &&
    scrubTargetBankId !== null &&
    scrubTargetBankId === activeBankId
  // Phase 2b — per-pad mixer bus routing for the active bank. Unset
  // entries default to master, which `resolveBusInput` handles.
  const padBusForActiveBank: Record<number, string> =
    (activeBankId ? container?.banks[activeBankId]?.padBus : undefined) ?? {}
  // Phase 5 — project-level conform toggle. When on, sample pads
  // with a known source BPM route through the Rubber Band worklet.
  // Gated on `!platform.isIPad` because WKWebView's AudioWorklet +
  // WASM stack doesn't actually stretch on iPad hardware (tested
  // deployed build: "awesome on web, gated on iPad"). Catalyst is
  // kept eligible because the macOS-embedded WebKit handles the
  // worklet correctly. Stored toggle state survives — it just
  // doesn't do anything on iPad until we ship a fix in Phase 5d.
  const platform = usePlatform()
  const conformToProjectBpm =
    container?.conformToProjectBpm === true && !platform.isIPad

  // Sample metadata lookup for sample-kind pads.
  const { data: samples = [] } = useAllSamples()
  const sampleById = useMemo(
    () => new Map(samples.map(s => [s.id, s])),
    [samples],
  )

  // Keys Mode needs to know which pad is focused so every pad can
  // play that pad's instrument transposed across the chromatic scale.
  // Reading `focusedPad` via selector (not destructured props) keeps
  // the hook re-evaluating when the user re-focuses a different pad.
  const focusedPad = useSequencerStore(s => s.focusedPad)

  // Kit voices resolved to the 16 pad slots.
  //
  // Keys Mode behaviour (producer-expected, matches Ableton Drum Rack
  // / Maschine / Logic samplers): every pad fires the **focused
  // pad's** sound — synth, sample, sampler-instrument, granular —
  // transposed by `(scaleNote - focusedNaturalNote)` semitones. The
  // MIDI-out path keeps the chromatic scale note + keys-mode channel.
  //
  // Prior behaviour rewrote every pad to a midi-kind synthetic, which
  // meant local audio was silent unless the user had an external MIDI
  // destination configured — a surprise silence bug.
  const kit = KITS[project?.kitId as KitId] ?? KITS.classic
  const resolvedPads = useMemo<Array<ResolvedPad | null>>(() => {
    if (!project) return []
    const kitVoiceList: Array<(typeof kit.voices)[number] | undefined> =
      new Array(PADS_PER_PROJECT).fill(undefined)
    kit.voices.forEach((voice, idx) => {
      kitVoiceList[idx] = voice
    })

    if (project.keysMode?.enabled) {
      const cfg = project.keysMode
      // Source pad locks at activation time (see KeysModeConfig.
      // sourcePadIndex) so subsequent pad taps can't swap the voice
      // out from under the user. Legacy projects without the field
      // fall back to focusedPad — matches pre-lock behaviour so
      // saved projects keep working until the user next toggles.
      const sourceIdx =
        typeof cfg.sourcePadIndex === 'number' ? cfg.sourcePadIndex : focusedPad
      const focused = resolvePad(project, kitVoiceList, sourceIdx)
      return Array.from({ length: PADS_PER_PROJECT }, (_, i) => {
        const scaleNote = keysModeNoteFor(i, cfg)
        // Focused pad empty — fall back to MIDI-only behaviour so
        // the hit at least produces a MIDI note for hardware.
        if (!focused) {
          return {
            kind: 'midi',
            label: midiNoteName(scaleNote),
            midiNote: scaleNote,
            channel: cfg.channel,
          }
        }
        const pitchOffset = scaleNote - focused.midiNote
        return {
          ...focused,
          midiNote: scaleNote,
          label: midiNoteName(scaleNote),
          // Keys-mode channel always overrides the focused pad's
          // channel so MIDI-out stays on the configured keys channel.
          channel: cfg.channel,
          keysModePitchSemitones: pitchOffset,
        }
      })
    }

    return Array.from({ length: PADS_PER_PROJECT }, (_, i) =>
      resolvePad(project, kitVoiceList, i),
    )
  }, [kit, project, focusedPad])

  /**
   * MIDI note-off delay for a step, in ms. Default (no custom length)
   * is 120ms — the legacy short gate. With `length` set, duration
   * scales with 16th-note count minus a small safety margin so
   * consecutive notes don't overlap at the sample boundary.
   */
  const noteOffDelayMs = useCallback(
    (length: number | undefined) => {
      if (!length || length <= 1) return 120
      const bpm = project?.bpm ?? 120
      const stepMs = 60_000 / bpm / 4
      return Math.max(40, length * stepMs - 20)
    },
    [project?.bpm],
  )

  /**
   * Single-hit pad fire. Handles the audio path (synth / sample / midi)
   * and the MIDI-out path. Rolls are expanded in `firePad` below —
   * `fireOneHit` never recurses.
   */
  const fireOneHit = useCallback(
    (padIndex: number, when: number, opts: FireOpts) => {
      if (!project) return
      // Per-pad mute short-circuit. Drops both the audio and MIDI
      // paths so an external instrument on a muted pad also stays
      // silent — matches what a mixer channel mute does.
      if (padMutedForActiveBank[padIndex]) return
      // Scrub-Pad bank override — while a zone is held / latched on
      // the Scrub Pad AND its target bank matches the current bank,
      // suppress pad audio so the scrub stream becomes the only
      // source for that bank's part (typical use: sliced drum break
      // on a target bank, scrub takes over while the rest of the
      // pattern plays on other banks).
      if (scrubOverrideActive) return
      const pad = resolvedPads[padIndex]
      if (!pad) return

      // Mixer bus routing (Phase 2b). Empty / missing entries route
      // through master — `resolveBusInput` inside the engine handles
      // the fallback. We look this up once per hit instead of on
      // every voice-schedule call to avoid a second closure.
      const busId = padBusForActiveBank[padIndex]

      // Keys Mode pitch offset layered on top of any step pitch lock.
      // Unset on normal fires → zero-cost fallback.
      const effectivePitch =
        (opts.pitch ?? 0) + (pad.keysModePitchSemitones ?? 0)

      // ── Audio path ──
      if (pad.kind === 'synth') {
        // Per-step envelope wraps the procedural voice's output in
        // a gain ADSR (see useAudioEngine.wrapWithEnvelope). Gate
        // length follows cell.length when set; otherwise the voice
        // plays its natural tail.
        const synthGateSec =
          opts.length !== undefined
            ? Math.max(0, opts.length * (60 / project.bpm / 4))
            : undefined
        trigger(project.kitId as KitId, pad.voice ?? null, when, {
          velocity: opts.velocity ?? 0.95,
          pitchSemitones: effectivePitch,
          fx: opts.fx,
          envelope: opts.envelope,
          gateSec: synthGateSec,
          busId,
        })
      } else if (pad.kind === 'instrument' && pad.instrumentId) {
        // Registry-backed instrument plugin. Scale velocity by the
        // pad's gain stack (sample + slice gain apply only when a
        // buffer-backed instrument like sampler points at a real
        // sample) and delegate to triggerInstrument — the engine
        // looks up the plugin, resolves any buffer from cache, and
        // builds + auto-disposes a voice.
        //
        // Automation (Wave 5c): when an automation lane targets
        // this pad's preset (`pad/<idx>/preset/<name>`), sample
        // the lane at the current step's tick and layer those
        // values on top of `pad.preset` before firing. Static
        // preset loses to live automation — the pad definition is
        // never mutated.
        let presetWithAutomation: typeof pad.preset = pad.preset
        if (typeof opts.stepIndex === 'number') {
          const snapshot = resolveAutomationAtTick(
            project,
            stepToTick(opts.stepIndex),
          )
          const overrides = extractPadPresetOverrides(snapshot)[padIndex]
          if (overrides && Object.keys(overrides).length > 0) {
            presetWithAutomation = {
              ...(pad.preset ?? {}),
              ...overrides,
            }
          }
        }
        const sampleMeta =
          pad.instrumentId === 'sampler' && pad.sampleId
            ? sampleById.get(pad.sampleId)
            : undefined
        const slice =
          pad.sliceId && sampleMeta?.slices
            ? sampleMeta.slices.find(s => s.id === pad.sliceId)
            : undefined
        const resolvedSliceStart =
          typeof pad.sliceStart === 'number'
            ? pad.sliceStart
            : slice
              ? slice.startSec
              : undefined
        const resolvedSliceLen =
          typeof pad.sliceLen === 'number'
            ? pad.sliceLen
            : slice
              ? slice.endSec - slice.startSec
              : undefined
        const padGainMult = gainStackLinear(
          sampleMeta?.gainDb,
          slice?.gainDb,
          pad.gainDb,
        )
        const gateSec =
          opts.length !== undefined
            ? Math.max(0.05, opts.length * (60 / project.bpm / 4))
            : undefined
        triggerInstrument(
          pad.instrumentId,
          when,
          (opts.velocity ?? 0.95) * padGainMult,
          effectivePitch,
          presetWithAutomation,
          busId,
          gateSec,
          sampleMeta?.playUrl
            ? {
                sampleId: sampleMeta.id,
                playUrl: sampleMeta.playUrl,
                sliceStart: resolvedSliceStart,
                sliceLen: resolvedSliceLen,
              }
            : undefined,
        )
      } else if (pad.kind === 'granular' && pad.sampleId) {
        // Granular pads read their raw params off `padDefs` — the
        // ResolvedPad shape only carries the common fields (label,
        // midiNote, sampleId/s3Key). Keeping the fire path lean:
        // sample lookup is shared with the sample branch, but the
        // granular config goes straight through to the engine.
        const rawDef = project.padDefs?.[padIndex]
        if (!rawDef || rawDef.kind !== 'granular') return
        const sampleMeta = sampleById.get(pad.sampleId)
        const gateSec =
          opts.length !== undefined
            ? Math.max(0, opts.length * (60 / project.bpm / 4))
            : undefined
        // Granular pads have no per-slice concept, so the stack is
        // velocity × sample.gainDb × pad.gainDb only.
        const granularGainMult = gainStackLinear(
          sampleMeta?.gainDb,
          undefined,
          rawDef.gainDb,
        )
        trigger(project.kitId as KitId, null, when, {
          velocity: (opts.velocity ?? 0.95) * granularGainMult,
          pitchSemitones: effectivePitch,
          fx: opts.fx,
          busId,
          granular: sampleMeta?.playUrl
            ? {
                sampleId: pad.sampleId,
                playUrl: sampleMeta.playUrl,
                grainSizeMs: rawDef.grainSizeMs,
                overlap: rawDef.overlap,
                detuneCents: rawDef.detuneCents,
                playbackRate: rawDef.playbackRate,
                reverse: rawDef.reverse,
                positionSec: rawDef.positionSec,
                positionSpread: rawDef.positionSpread,
                pitchSpread: rawDef.pitchSpread,
                loop: rawDef.loop,
                envelope: opts.envelope,
                gateSec,
              }
            : undefined,
        })
      } else if (pad.kind === 'sample' && pad.sampleId) {
        const sampleMeta = sampleById.get(pad.sampleId)
        // Scrambler remaps the pad's slice through a deterministic
        // permutation of the sample's slice list. With scrambleSeed=0
        // (the default) this is a pass-through, so sessions without
        // the Scrambler engaged pay zero cost.
        const scrambledId = sampleMeta?.slices?.length
          ? scrambledSliceId(
              pad.sliceId,
              sampleMeta.slices.map(s => s.id),
              padIndex,
              scramblerParams,
              pad.sampleId,
              busId,
            )
          : pad.sliceId
        const slice = scrambledId
          ? sampleMeta?.slices?.find(s => s.id === scrambledId)
          : undefined
        // Gate semantics — see PadContent.playback on the shared
        // sample-pad type:
        //   - `'one-shot'` (default): never gate from cell.length;
        //     the slice/sample plays to its natural end. Matches how
        //     chopped-break pads behaved when cell.length was
        //     undefined (the default).
        //   - `'gated'`: respect cell.length when set.
        //
        // The offline renderer (renderIntoOfflineContext in
        // useAudioEngine.ts) mirrors this logic; both paths must
        // agree or exports drift from live.
        const playbackMode = pad.playback ?? 'one-shot'
        const gateSec =
          playbackMode === 'gated' && opts.length !== undefined
            ? Math.max(0, opts.length * (60 / project.bpm / 4))
            : undefined
        // Multiplicative gain stack: velocity × sample.gainDb ×
        // slice.gainDb × pad.gainDb. Applied only to the audio
        // path — the MIDI-out branch below keeps the unscaled
        // opts.velocity so hardware note velocity stays intact.
        const gainMult = gainStackLinear(
          sampleMeta?.gainDb,
          slice?.gainDb,
          pad.gainDb,
        )
        trigger(project.kitId as KitId, null, when, {
          velocity: (opts.velocity ?? 0.95) * gainMult,
          pitchSemitones: effectivePitch,
          fx: opts.fx,
          busId,
          sample: sampleMeta?.playUrl
            ? {
                sampleId: pad.sampleId,
                playUrl: sampleMeta.playUrl,
                sliceStart: slice?.startSec,
                sliceLenSec: slice
                  ? Math.max(0, slice.endSec - slice.startSec)
                  : undefined,
                reverse: opts.reverse,
                timeStretch: opts.timeStretch,
                envelope: opts.envelope,
                gateSec,
                // Phase 5 — only send conform params when the
                // toggle is on AND the sample has a source BPM.
                // Missing either → playbackRate fallback in the
                // engine.
                sourceBpm:
                  conformToProjectBpm && sampleMeta.bpm
                    ? sampleMeta.bpm
                    : undefined,
                conformBpm: conformToProjectBpm ? project.bpm : undefined,
              }
            : undefined,
        })
      }

      // ── MIDI-out path ──
      if (opts.sendMidi !== false && project.midiEnabled) {
        const keysOn = project.keysMode?.enabled === true
        const padRouting: PadMidiRouting | undefined = keysOn
          ? project.keysMode?.output
          : project.padOutputs?.[padIndex]
        let resolved: 'skip' | 'broadcast' | { outputId: string }
        if (padRouting === 'off') {
          resolved = 'skip'
        } else if (padRouting === 'all') {
          resolved = 'broadcast'
        } else if (padRouting === undefined) {
          // Default routing: broadcast when Keys Mode is on (every
          // pad is a "MIDI note") or when the pad is explicitly
          // midi-kind; skip otherwise (synth / sample / etc. don't
          // implicitly send MIDI). Without the Keys Mode branch,
          // transposed synth/sample pads would silently drop MIDI.
          resolved = keysOn || pad.kind === 'midi' ? 'broadcast' : 'skip'
        } else {
          resolved = { outputId: padRouting }
        }

        if (resolved !== 'skip') {
          const midiVel = Math.round(MIDI_VELOCITY * (opts.velocity ?? 1))
          // Use the resolved pad's channel when present — Keys Mode
          // stamps `cfg.channel` onto every pad regardless of kind,
          // so this covers both `kind: 'midi'` pads and Keys-mode
          // transposed pads without special-casing either.
          const channelBits =
            pad.channel !== undefined
              ? Math.max(0, Math.min(15, pad.channel - 1))
              : 0
          const noteOn = [
            0x90 | channelBits,
            pad.midiNote,
            Math.max(1, Math.min(127, midiVel)),
          ]
          const noteOff = [0x80 | channelBits, pad.midiNote, 0]
          const latency =
            typeof resolved === 'object'
              ? (outputLatencyMs[resolved.outputId] ?? 0)
              : 0
          const offsetMs = Math.max(0, (when - currentTime()) * 1000 - latency)
          const sendAt = (data: number[], delayMs: number) => {
            const fire = () => {
              if (resolved === 'broadcast') {
                dispatchVirtualMidi(VIRTUAL_SEQUENCER_INPUT_ID, data)
              } else if (typeof resolved === 'object') {
                dispatchToHardwareOutput(resolved.outputId, data)
              }
            }
            if (delayMs <= 0) fire()
            else window.setTimeout(fire, delayMs)
          }
          sendAt(noteOn, offsetMs)
          sendAt(noteOff, offsetMs + noteOffDelayMs(opts.length))
        }
      }
      // Skip the refocus when Keys mode is on: the source voice is
      // locked at activation time (`keysMode.sourcePadIndex`), and
      // refocusing would flip a bunch of unrelated UI (Inspector,
      // Cell Editor Pad tab, Piano Roll) onto whichever pad the
      // keyboard player happens to tap last. Users still long-press
      // a pad to jump focus explicitly — that path doesn't come
      // through here.
      if (opts.focusOnClick && !project?.keysMode?.enabled) {
        setFocusedPad(padIndex)
      }
    },
    [
      currentTime,
      dispatchVirtualMidi,
      dispatchToHardwareOutput,
      noteOffDelayMs,
      outputLatencyMs,
      padMutedForActiveBank,
      padBusForActiveBank,
      project,
      resolvedPads,
      sampleById,
      scrubOverrideActive,
      setFocusedPad,
      trigger,
    ],
  )

  /**
   * Public fire entry point. Adds roll fan-out on top of `fireOneHit`.
   * When `opts.roll` is set, splits the step's time window into N
   * equal hits with linearly-interpolated pitch + velocity.
   */
  const firePad = useCallback(
    (padIndex: number, when: number, opts: FirePadOpts = {}) => {
      if (!project) return
      const { roll, ...baseOpts } = opts
      if (!roll) {
        fireOneHit(padIndex, when, baseOpts)
        return
      }
      const n = roll.subdivisions
      const stepDurSec = 60 / project.bpm / 4
      const hitDurSec = stepDurSec / n
      const basePitch = baseOpts.pitch ?? 0
      const baseVel = baseOpts.velocity ?? 1
      const pStart = roll.pitchStart ?? basePitch
      const pEnd = roll.pitchEnd ?? pStart
      const vStart = roll.velocityStart ?? baseVel
      const vEnd = roll.velocityEnd ?? vStart
      // B4 — per-hit overrides. `pitchOffsets[i]` wins over the
      // linear ramp when present; `subdivisionEnabled[i] === false`
      // skips the hit entirely. Shorter arrays fall back to the
      // ramp / enabled=true for unspecified indices.
      const pitchOffsets = roll.pitchOffsets
      const enabledMask = roll.subdivisionEnabled
      for (let i = 0; i < n; i++) {
        if (enabledMask && enabledMask[i] === false) continue
        const progress = n <= 1 ? 0 : i / (n - 1)
        const offset = pitchOffsets?.[i]
        const hitPitch =
          offset !== undefined ? offset : pStart + (pEnd - pStart) * progress
        // Tail-delay marker: merge a one-shot send to the master
        // delay onto the LAST sub-hit's `fx`. Gives the roll a
        // natural trail into silent steps without requiring users
        // to hand-automate the delay send.
        const tailFx =
          roll.tailDelay && i === n - 1
            ? {
                ...(baseOpts.fx ?? {}),
                delay: Math.max(0.7, (baseOpts.fx?.delay ?? 0) + 0.7),
              }
            : baseOpts.fx
        fireOneHit(padIndex, when + i * hitDurSec, {
          ...baseOpts,
          fx: tailFx,
          velocity: vStart + (vEnd - vStart) * progress,
          pitch: hitPitch,
          length: (baseOpts.length ?? 1) / n,
        })
      }
    },
    [fireOneHit, project],
  )

  /**
   * Held-note attack for a pad. Only instrument-kind pads support
   * true hold semantics — their voices expose a `.release()` method
   * that we can call when the key comes up. Synth / sample / granular
   * pads do a one-shot `firePad` and return null because their
   * underlying voices are gate-driven and don't respond to later
   * release events.
   *
   * Returns an opaque voice token for instrument pads (pass to
   * `releasePad` on note-off) or null for everything else.
   *
   * Also early-returns null for muted pads / scrub-override / empty
   * pads / sampler pads (slice length IS the gate there).
   */
  const attackPad = useCallback(
    (
      padIndex: number,
      opts: { velocity?: number; pitch?: number } = {},
    ): number | null => {
      if (!project) return null
      if (padMutedForActiveBank[padIndex]) return null
      if (scrubOverrideActive) return null
      const pad = resolvedPads[padIndex]
      if (!pad) return null

      if (
        pad.kind !== 'instrument' ||
        !pad.instrumentId ||
        pad.instrumentId === 'sampler'
      ) {
        // Non-holdable: one-shot fire, no token.
        firePad(padIndex, currentTime(), {
          velocity: opts.velocity,
          pitch: opts.pitch,
          sendMidi: false,
        })
        return null
      }

      const busId = padBusForActiveBank[padIndex]
      const effectivePitch =
        (opts.pitch ?? 0) + (pad.keysModePitchSemitones ?? 0)
      const padGainMult = gainStackLinear(undefined, undefined, pad.gainDb)
      return attackInstrument(
        pad.instrumentId,
        (opts.velocity ?? 0.95) * padGainMult,
        effectivePitch,
        pad.preset,
        busId,
      )
    },
    [
      project,
      padMutedForActiveBank,
      scrubOverrideActive,
      resolvedPads,
      padBusForActiveBank,
      firePad,
      currentTime,
      attackInstrument,
    ],
  )

  const releasePad = useCallback(
    (token: number | null) => {
      if (token === null) return
      releaseInstrument(token)
    },
    [releaseInstrument],
  )

  /**
   * Fire a custom (user-added) MIDI pad. No synth voice — the pad
   * exists only to emit MIDI on a chosen note + channel.
   */
  const fireMidiPad = useCallback(
    (
      pad: Pick<MidiPadDef, 'note' | 'channel'> & { outputId?: string },
      when: number,
      opts: {
        velocity?: number
        length?: number
        sendMidi?: boolean
      } = {},
    ) => {
      if (!project) return
      if (opts.sendMidi === false || !project.midiEnabled) return
      if (pad.outputId === 'off') return
      const ch = Math.max(0, Math.min(15, pad.channel - 1))
      const midiVel = Math.round(MIDI_VELOCITY * (opts.velocity ?? 1))
      const noteOn = [0x90 | ch, pad.note, Math.max(1, Math.min(127, midiVel))]
      const noteOff = [0x80 | ch, pad.note, 0]
      const latency =
        pad.outputId && pad.outputId !== 'all'
          ? (outputLatencyMs[pad.outputId] ?? 0)
          : 0
      const offsetMs = Math.max(0, (when - currentTime()) * 1000 - latency)
      const sendAt = (data: number[], atMs: number) => {
        const fire = () => {
          if (pad.outputId && pad.outputId !== 'all') {
            dispatchToHardwareOutput(pad.outputId, data)
          } else {
            dispatchVirtualMidi(VIRTUAL_SEQUENCER_INPUT_ID, data)
          }
        }
        if (atMs <= 0) fire()
        else window.setTimeout(fire, atMs)
      }
      sendAt(noteOn, offsetMs)
      sendAt(noteOff, offsetMs + noteOffDelayMs(opts.length))
    },
    [
      currentTime,
      dispatchVirtualMidi,
      dispatchToHardwareOutput,
      noteOffDelayMs,
      outputLatencyMs,
      project,
    ],
  )

  return {
    /** Resolved 16-pad layout (post Keys Mode). */
    resolvedPads,
    /** Trigger a pad with optional roll fan-out. */
    firePad,
    /** Hold-note attack on an instrument pad. Returns a voice token
     *  for instrument kinds, or null for non-holdable pads (sample,
     *  synth, granular, sampler — those one-shot through firePad). */
    attackPad,
    /** Release a voice token returned from attackPad. Safe on null. */
    releasePad,
    /** Trigger a custom MIDI-only overflow pad. */
    fireMidiPad,
    /** Resume the AudioContext — call from user-gesture handlers. */
    resume,
    /** Current AudioContext time — for scheduling decisions. */
    currentTime,
    /** The live padNoteBindings map (MIDI Learn overrides). */
    padNoteBindings,
  }
}

/** Result type exported so the PlaybackProvider can type its handlers. */
export type SequencerTriggers = ReturnType<typeof useSequencerTriggers>
